import React from 'react';
import styled, { useTheme } from 'styled-components/macro';
import { sizesMap, textLineHeight } from '../../typography/utils';
import { SPACINGS } from '../../utils/style';
import { Text } from '../../typography';

type InputType = 'default' | 'currency' | 'number' | 'password';

function handleInputType(type: InputType) {
  switch (type) {
    case 'currency':
    case 'number':
      return 'number';
    case 'password':
      return 'password';
    default:
      return '';
  }
}

const InputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: ${(p) => p.theme.dimensions.inputWidth};
`;

type InputStyledProps = {
  error: boolean;
  componentType: InputType;
  baseColor: string;
  mainColor: string;
  accentColor: string;
  inactiveColor: string;
  dangerColor: string;
  backgroundColor: string;
};
const InputStyled = styled.input<InputStyledProps>`
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;

  background-clip: padding-box;

  margin: 0;
  margin-bottom: ${(p) => (p.error ? SPACINGS[2] : 0)};
  padding: 0 ${SPACINGS[5]};

  background-color: ${(p) => p.backgroundColor};
  height: ${SPACINGS[10]};
  width: 100%;
  border: 1px solid ${(p) => (p.error ? p.dangerColor : p.baseColor)};
  border-radius: ${(p) => p.theme.dimensions.borderRadius};

  color: ${(p) => p.mainColor};
  font-family: ${(p) => p.theme.fonts.gtEesti};
  font-size: ${sizesMap['base'].size};
  line-height: ${textLineHeight};
  text-align: ${(p) =>
    handleInputType(p.componentType) === 'number' ? 'right' : 'left'};

  &::placeholder {
    color: ${(p) => p.baseColor};
  }

  &:disabled {
    background-color: ${(p) => p.inactiveColor};
    &:hover {
      cursor: not-allowed;
    }
  }

  &:read-only {
    background-color: ${(p) => p.inactiveColor};
    &:hover {
      cursor: not-allowed;
    }
  }

  &:not(:disabled) {
    &:hover {
      border: 1px solid ${(p) => (p.error ? p.dangerColor : p.mainColor)};
    }
    &:focus {
      outline: none;
      border: 1px solid ${(p) => (p.error ? p.dangerColor : p.mainColor)};
      box-shadow: 0 0 0 2px ${(p) => p.backgroundColor},
        0 0 0 3px ${(p) => p.accentColor};
    }
  }

  &[type='number']::-webkit-inner-spin-button,
  &[type='number']::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`;

const CurrencyStyled = styled.div`
  display: inline-flex;
  position: absolute;
  align-items: center;
  height: ${SPACINGS[10]};
  padding: ${SPACINGS[5]};
`;

export type InputProps = {
  name: string;
  error?: string | boolean;
  inputRef?:
    | ((instance: HTMLInputElement | null) => void)
    | React.RefObject<HTMLInputElement>;
  style?: React.CSSProperties;
  defaultValue?: string;
  value?: string;
  onChange?: ((event: React.ChangeEvent<HTMLInputElement>) => void) | undefined;
  autoFocus?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  type?: InputType;
  currency?: string;
  placeholder?: string;
  textColor?: string;
  baseColor?: string;
  mainColor?: string;
  accentColor?: string;
  inactiveColor?: string;
  dangerColor?: string;
  backgroundColor?: string;
};

function Input(props: InputProps) {
  const theme = useTheme();

  const {
    name,
    inputRef,
    error,
    style,
    defaultValue,
    value,
    onChange,
    autoFocus,
    disabled,
    readOnly,
    type = 'default',
    currency,
    placeholder,
    textColor,
    baseColor = theme.colors.secondary80w,
    mainColor = theme.colors.black,
    accentColor = theme.colors.primary,
    inactiveColor = theme.colors.secondary90w,
    dangerColor = theme.colors.danger,
    backgroundColor = theme.colors.white,
  } = props;

  const errorStyle = React.useMemo(() => ({ color: theme.colors.danger }), [
    theme,
  ]);

  return (
    <InputWrapper style={style}>
      {type === 'currency' && currency && (
        <CurrencyStyled>
          <Text style={{ color: textColor }} margin="none">
            {currency}
          </Text>
        </CurrencyStyled>
      )}
      <InputStyled
        componentType={type}
        type={handleInputType(type)}
        id={name}
        name={name}
        error={Boolean(error)}
        ref={inputRef}
        defaultValue={defaultValue}
        value={value}
        onChange={onChange}
        autoFocus={autoFocus}
        disabled={disabled}
        readOnly={readOnly}
        placeholder={placeholder}
        baseColor={baseColor}
        mainColor={textColor ?? mainColor}
        accentColor={accentColor}
        inactiveColor={inactiveColor}
        dangerColor={dangerColor}
        backgroundColor={backgroundColor}
      />
      {typeof error === 'string' && (
        <Text id={`${name}-error`} style={errorStyle} margin="none">
          {error}
        </Text>
      )}
    </InputWrapper>
  );
}

export default React.memo(Input);
